import { Fade } from '@chakra-ui/react';
import React, { useRef } from 'react';

import CreditBureauLinks from '../../../../components/CreditBureauLinks';
import Loader from '../../../../components/Loader';
import SelfServePayoffModal from '../../../../components/SelfServePayoffModal';
import { DealType } from '../../../../gql/generated/graphql';
import useLeaseExpertOpts from '../../../../hooks/useLeaseExpertOpts';
import { sameEmail } from '../../../../utils/customer';
import { DealStateEnum } from '../../../../utils/types/deal';
import { DOWN_PAYMENT_STATES, DealWithFarthestState } from '../../utils';
import SocialProofCard from '../social-proof/SocialProofCard';
import CobuyerAcknowledgmentBanner from './CobuyerAcknowledgmentBanner';
import HaveQuestionsCTA from './HaveQuestionsCTA';
import Header from './Header';
import SoftCloseMessage from './SoftCloseMessage';
import StepperDocumentsCard from './StepperDocumentsCard';
import StepperDownPaymentCard from './StepperDownPaymentCard';
import StepperLayout, { StepperSidebarItem } from './StepperLayout';
import DealStepper from './dealStepper/DealStepper';
import getCurrentStepActions from './getCurrentStepActions';
import useCreditStatus from './hooks/useCreditStatus';
import useSelfServePayoffModal from './hooks/useSelfServePayoffModal';
import useStepperAnimation from './hooks/useStepperAnimation';
import useStepperState from './hooks/useStepperState';
import { getHeaderContent } from './utils';

type StepperDashboardProps = {
  deal: DealWithFarthestState;
};

const StepperDashboard = ({ deal }: StepperDashboardProps) => {
  const dealState = deal?.state as DealStateEnum;
  const farthestState = deal?.farthestState as DealStateEnum;
  const isSoftClose = farthestState === DealStateEnum.SoftClose;
  const showDownPayment =
    (deal?.financial_info?.money_down ?? 0) > 0 && DOWN_PAYMENT_STATES.includes(dealState);

  const { creditStatus, isFrozenModalOpen, setIsFrozenModalOpen } = useCreditStatus(deal);
  const leaseExpertOpts = useLeaseExpertOpts(deal);
  const {
    activeStep,
    payoffStepStatus,
    payoffSource,
    is5StepVariant,
    payoffLoading,
    refetchPayoff,
  } = useStepperState(deal, leaseExpertOpts.isOpen);
  const selfServePayoff = useSelfServePayoffModal({
    dealId: deal?.id,
    payoffStepStatus,
    payoffSource,
    refetchPayoff,
  });
  const { animate, show } = useStepperAnimation(deal, activeStep, payoffLoading);

  const eSignRef = useRef<HTMLDivElement>(null);
  const scrollToESign = () => eSignRef.current?.scrollIntoView({ behavior: 'smooth' });

  const { header, subtitle, onSubtitleClick } = getHeaderContent(
    isSoftClose ? DealStateEnum.SoftClose : dealState,
    { onSubtitleClick: scrollToESign },
  );

  const lienholder = deal?.car?.payoff?.lienholder_name;
  const lienholderSlug = deal?.car?.payoff?.lienholder_slug;

  if (!deal || payoffLoading) {
    return <Loader />;
  }

  const sidebarItems: StepperSidebarItem[] = [
    ...(showDownPayment
      ? [
          {
            key: 'down-payment',
            node: <StepperDownPaymentCard moneyDown={deal?.financial_info?.money_down ?? 0} />,
          },
        ]
      : []),
    { key: 'documents', node: <StepperDocumentsCard deal={deal} /> },
    { key: 'social-proof', node: <SocialProofCard dealId={deal?.id ?? ''} /> },
    { key: 'have-questions', node: <HaveQuestionsCTA /> },
  ];

  return (
    <Fade in={show} transition={{ enter: { duration: animate ? 4 : 0 } }}>
      <StepperLayout
        header={
          <Header
            header={header}
            subtitle={subtitle}
            isCobuyer={deal?.isCobuyer}
            isSoftClose={isSoftClose}
            vin={deal?.car?.vin}
            onSubtitleClick={onSubtitleClick}
          />
        }
        sidebarItems={sidebarItems}
      >
        <CobuyerAcknowledgmentBanner deal={deal} />
        {isSoftClose ? (
          <SoftCloseMessage isRefi={deal?.type === DealType.Refi} />
        ) : (
          <DealStepper
            activeStep={activeStep}
            lienholder={lienholder}
            variant={is5StepVariant ? '5step' : '4step'}
            creditStatus={creditStatus}
            onFrozenClick={() => setIsFrozenModalOpen(true)}
            currentStepActions={getCurrentStepActions(deal)}
            animate={animate}
            leaseExpertOpts={leaseExpertOpts}
            eSignRef={eSignRef}
            payoffStepStatus={payoffStepStatus}
            payoffSource={payoffSource}
            onPayoffLookup={selfServePayoff.open}
            hasSharedEmail={sameEmail(deal?.customer, deal?.cobuyer)}
          />
        )}
        <CreditBureauLinks
          asModal
          separateLinkFromIcon
          containerProps={{
            flexDir: { base: 'column', md: 'row' },
            alignItems: 'center',
          }}
          modalProps={{
            title: 'Your credit is frozen or locked',
            subtitle:
              "Unfreeze or unlock your credit at the bureau(s) where you've placed a restriction — each bureau's process is quick.",
            isOpen: isFrozenModalOpen,
            onClose: () => setIsFrozenModalOpen(false),
          }}
        />
      </StepperLayout>
      <SelfServePayoffModal
        isOpen={selfServePayoff.isOpen}
        onClose={selfServePayoff.close}
        title="Your Payoff Amount"
        description={
          <>
            We couldn’t pull your payoff from <strong>{lienholder || 'your lienholder'}</strong>.
            Look it up and enter it now to move faster, or our team will help next business day.
          </>
        }
        dismissLabel="I'll wait for your team"
        lienholderName={lienholder}
        lienholderSlug={lienholderSlug}
        retailBookValue={deal?.car?.retail_book_value}
        submitting={selfServePayoff.submitting}
        onSubmit={selfServePayoff.submit}
      />
    </Fade>
  );
};

export default StepperDashboard;
